import axios from 'axios';
import buildPaginationQueryOpts from '@/shared/sort/sorts';
import { IRestaurant } from '@/shared/model/restaurant.model';
import { IMenu } from '../model/menu.model';
import { IAddonCategory } from '../model/addon-category.model';
import { IProductInfo } from '../model/product-info.model';
import { IOrderDetail } from '../model/order/order-detail.model';

const baseApiUrl = 'api/restaurants';
const baseAdminApiUrl = 'api/admin';
const basePublicApiUrl = 'api/public/restaurants';
const baseSearchApiUrl = 'api/_search/restaurants?query=';
const baseSupportRestaurantApiUrl = 'api/support/restaurant';
const baseSupportRestaurantsApiUrl = 'api/support/restaurants';

export default class RestaurantService {
  public search(query): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${baseSearchApiUrl}${query}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public find(id: number): Promise<IRestaurant> {
    return new Promise<IRestaurant>((resolve, reject) => {
      axios
        .get(`${baseSupportRestaurantApiUrl}/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getRestaurant(id: number, zipcode: number): Promise<IRestaurant> {
    return new Promise<IRestaurant>((resolve, reject) => {
      axios
        .get(`${basePublicApiUrl}/${id}/${zipcode}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findByUserId(id: number): Promise<IRestaurant> {
    return new Promise<IRestaurant>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/user/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieve(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(baseApiUrl)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveRestaurants(req?: any): Promise<any> {
    return axios.get(`api/support/restaurants?${buildPaginationQueryOpts(req)}`);
  }
  public retrieveNewRestaurants(req?: any): Promise<any> {
    return axios.get(`api/support/newRestaurants?${buildPaginationQueryOpts(req)}`);
  }
  public retrieveUnregisteredRestaurants(): Promise<any> {
    return axios.get(`api/support/users/restaurants`);
  }
  public retrieveUpdatedRestaurants(req?: any): Promise<any> {
    return axios.get(`api/support/restaurants/updated?${buildPaginationQueryOpts(req)}`);
  }

  public retrievePublic(postal_code): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${basePublicApiUrl}/list/${postal_code}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getDocuments(restaurant: IRestaurant, field: string): Promise<any> {
    return axios.get(`api/document/restaurant/${restaurant.id}/${field}/${restaurant[field + 'Path']}`);
  }

  public getMenus(menuCategoryId: number): Promise<IMenu[]> {
    return new Promise<IMenu[]>((resolve, reject) => {
      axios
        .get(`api/public/menus/${menuCategoryId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getMenuInfo(id: number): Promise<IProductInfo> {
    return new Promise<IProductInfo>((resolve, reject) => {
      axios
        .get(`api/public/menuinfo/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getAddonInfo(id: number): Promise<{ additives: string[]; allergens: string[] }> {
    return new Promise<IProductInfo>((resolve, reject) => {
      axios
        .get(`api/public/addoninfo/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getAddonCategories(id: number): Promise<IAddonCategory[]> {
    return new Promise<IAddonCategory[]>((resolve, reject) => {
      axios
        .get(`api/public/addon-categories/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getReadyOrders(id: number): Promise<IOrderDetail[]> {
    return new Promise<IOrderDetail[]>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/readyorders/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getPreparingOrders(id: number): Promise<IOrderDetail[]> {
    return new Promise<IOrderDetail[]>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/preparingorders/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getSentOrders(id: number, req?: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/sentorders/${id}?${buildPaginationQueryOpts(req)}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findByOrderId(orderId: number): Promise<IOrderDetail> {
    return new Promise<IOrderDetail>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/order/${orderId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public uploadRestaurantDocument(id: number, fieldName: string, files): Promise<boolean> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/document/${id}/${fieldName}`, files)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public callRestaurant(id: number): Promise<boolean> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${basePublicApiUrl}/call-restaurant/${id}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public deleteRestaurant(id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(`${baseAdminApiUrl}/delete-restaurant/${id}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public delete(id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(`${baseApiUrl}/${id}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public deleteRestaurantDocument(id: number, field: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(`${baseSupportRestaurantsApiUrl}/document/${id}/${field}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public deleteRestaurantMenuCard(id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(`${baseApiUrl}/document/menuCard/${id}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public create(entity: IRestaurant): Promise<IRestaurant> {
    return new Promise<IRestaurant>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public update(entity: IRestaurant): Promise<IRestaurant> {
    return new Promise<IRestaurant>((resolve, reject) => {
      axios
        .put(`${baseApiUrl}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public updateDiscounts(entity: IRestaurant): Promise<IRestaurant> {
    const restaurant = { ...entity };
    restaurant.marketer = null;

    return new Promise<IRestaurant>((resolve, reject) => {
      axios
        .put(`${baseApiUrl}`, restaurant)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public updateMenuCategories(entity: IRestaurant): Promise<IRestaurant> {
    const restaurant = { ...entity };

    if (!restaurant.marketer.id) {
      restaurant.marketer = null;
    }

    restaurant.updated = true;

    return new Promise<IRestaurant>((resolve, reject) => {
      axios
        .put(`${baseApiUrl}`, restaurant)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public confirmOrder(orderId: number, preparingTime: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/confirmorder/${orderId}/${preparingTime}`)
        .then(res => resolve(true))
        .catch(err => reject(false));
    });
  }

  public orderPrepared(orderId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/orderprepared/${orderId}`)
        .then(res => resolve(true))
        .catch(err => reject(false));
    });
  }

  public allOrdersPrepared(restaurantId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/allordersprepared/${restaurantId}`)
        .then(res => resolve(true))
        .catch(err => reject(false));
    });
  }

  public setRestaurantApproval(restaurantId: number, decision: boolean): Promise<IRestaurant> {
    return new Promise<IRestaurant>((resolve, reject) => {
      axios
        .patch(`${baseSupportRestaurantApiUrl}/approval/${restaurantId}?decision=${decision}`, null, {
          headers: { 'content-type': 'application/merge-patch+json' },
        })
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findByOrderNumberEnc(orderNumberEnc: string): Promise<IOrderDetail> {
    return new Promise<IOrderDetail>((resolve, reject) => {
      axios
        .get(`${basePublicApiUrl}/ordernumberenc/${orderNumberEnc}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public setRestaurantActivation(restaurantId: number, decision: boolean): Promise<IRestaurant> {
    return new Promise<IRestaurant>((resolve, reject) => {
      axios
        .patch(`${baseSupportRestaurantApiUrl}/activation/${restaurantId}`, decision, {
          headers: { 'content-type': 'application/merge-patch+json' },
        })
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public setReleased(restaurantId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .put(`${baseSupportRestaurantApiUrl}/released/${restaurantId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public createOnboardingLink(id: number): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/onboard-stripe/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public checkStripeStatus(id: number): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/check-stripe/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public refreshOnboarding(id: number): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/refresh-stripe/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getMenuStandardAssets(): Promise<string[]> {
    return new Promise<string[]>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/assets/menu-standard`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
}
